import { create } from 'zustand';
import { pancakeApi } from '../services/pancake-api';
import type {
  PancakePage,
  PancakeOverview,
  PancakeConversation,
  PancakeMessage,
  PancakeCustomer,
  PancakePost,
  PancakeCampaignStats,
  PancakeEngagementStats,
  PancakeTagStats,
  PancakeStaffStats,
} from '../types/pancake';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';

type ActiveView = 'overview' | 'conversations' | 'statistics' | 'customers' | 'posts';

type FetchKey = string;

interface PancakeState {
  fromDate: string;
  toDate: string;
  activeView: ActiveView;
  selectedPageId: string | 'all';

  overview: PancakeOverview | null;
  conversations: PancakeConversation[];
  conversationMessages: PancakeMessage[];
  customers: { total: number; customers: PancakeCustomer[] };
  posts: { total: number; posts: PancakePost[] };
  statsCampaigns: PancakeCampaignStats[];
  statsAds: PancakeCampaignStats[];
  statsEngagement: PancakeEngagementStats[];
  statsPagePerformance: PancakeEngagementStats[];
  statsTags: PancakeTagStats[];
  statsStaff: PancakeStaffStats[];
  tags: { id: string; name: string }[];
  pages: PancakePage[];

  // The date range that was actually used for the last successful fetch
  reportFrom: string;
  reportTo: string;

  // Tracks which params each view was last fetched with
  fetchedKeys: Partial<Record<ActiveView, FetchKey>>;

  // Loading states
  isLoading: boolean;
  error: string | null;

  // Actions
  setDateRange: (from: string, to: string) => void;
  setActiveView: (view: ActiveView) => void;
  setSelectedPageId: (id: string | 'all') => void;
  fetchDashboardData: (force?: boolean) => Promise<void>;
  fetchConversationMessages: (pageId: string, conversationId: string) => Promise<void>;
}

const now = new Date();
const lastMonth = subMonths(now, 1);
const defaultFrom = format(startOfMonth(lastMonth), 'yyyy-MM-dd');
const defaultTo = format(endOfMonth(lastMonth), 'yyyy-MM-dd');

export const usePancakeStore = create<PancakeState>((set, get) => ({
  fromDate: defaultFrom,
  toDate: defaultTo,
  activeView: 'overview',
  selectedPageId: 'all',

  overview: null,
  conversations: [],
  conversationMessages: [],
  customers: { total: 0, customers: [] },
  posts: { total: 0, posts: [] },
  statsCampaigns: [],
  statsAds: [],
  statsEngagement: [],
  statsPagePerformance: [],
  statsTags: [],
  statsStaff: [],
  tags: [],
  pages: [],

  reportFrom: defaultFrom,
  reportTo: defaultTo,
  fetchedKeys: {},

  isLoading: false,
  error: null,

  setDateRange: (from, to) => {
    set({ fromDate: from, toDate: to });
  },

  setActiveView: (view) => {
    set({ activeView: view });
  },

  setSelectedPageId: (id) => {
    set({ selectedPageId: id });
  },

  fetchDashboardData: async (force = false) => {
    const { fromDate, toDate, selectedPageId, activeView, fetchedKeys } = get();
    const currentKey = `${fromDate}|${toDate}|${selectedPageId}`;

    // Skip fetch if this view already has data for the same params
    if (!force && fetchedKeys[activeView] === currentKey) return;

    set({ isLoading: true, error: null });

    try {
      const params = { from: fromDate, to: toDate };

      // Fetch pages & tags once (they don't depend on date range)
      const { pages, tags } = get();
      const metaPromises: Promise<void>[] = [];
      if (pages.length === 0 || force) {
        metaPromises.push(pancakeApi.getPages().then(p => set({ pages: p })));
      }
      if (tags.length === 0 || force) {
        metaPromises.push(pancakeApi.getTags().then(t => set({ tags: t })));
      }

      if (activeView === 'overview') {
        const [overviewData] = await Promise.all([
          pancakeApi.getOverview(params),
          ...metaPromises,
        ]);
        set({ overview: overviewData, isLoading: false });
      } else if (activeView === 'conversations') {
        const [conversations] = await Promise.all([
          pancakeApi.getConversations(params),
          ...metaPromises,
        ]);
        set({ conversations, isLoading: false });
      } else if (activeView === 'statistics') {
        const [
          statsCampaigns,
          statsAds,
          statsEngagement,
          statsPagePerformance,
          statsTags,
          statsStaff,
        ] = await Promise.all([
          pancakeApi.getCampaignStats(params),
          pancakeApi.getAdsStats(params),
          pancakeApi.getEngagementStats(params),
          pancakeApi.getPagePerformance(params),
          pancakeApi.getTagStats(params),
          pancakeApi.getStaffStats(params),
          ...metaPromises,
        ]);
        set({
          statsCampaigns, statsAds, statsEngagement,
          statsPagePerformance, statsTags, statsStaff,
          isLoading: false,
        });
      } else if (activeView === 'customers') {
        const [customersData] = await Promise.all([
          pancakeApi.getCustomers(params),
          ...metaPromises,
        ]);
        set({ customers: customersData, isLoading: false });
      } else if (activeView === 'posts') {
        const [postsData] = await Promise.all([
          pancakeApi.getPosts(params),
          ...metaPromises,
        ]);
        set({ posts: postsData, isLoading: false });
      }

      // Mark this view as fetched and record the date range used
      set(state => ({
        fetchedKeys: { ...state.fetchedKeys, [activeView]: currentKey },
        reportFrom: fromDate,
        reportTo: toDate,
      }));
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch Pancake data', isLoading: false });
    }
  },

  fetchConversationMessages: async (pageId, conversationId) => {
    set({ isLoading: true, error: null });
    try {
      const conversationMessages = await pancakeApi.getConversationMessages(pageId, conversationId);
      set({ conversationMessages, isLoading: false });
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch conversation messages', isLoading: false });
    }
  },
}));
